import { useEffect, useRef, useState } from "react";
import { FiMapPin, FiNavigation, FiCheck } from "react-icons/fi";

const OptionalLocation = ({ location, onChange, isDarkMode }) => {
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [permissionState, setPermissionState] = useState("idle"); // idle | requesting | granted | denied
  const [error, setError] = useState("");
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // This component mounts when Sell Now reveals the form. Automatically ask
  // for location permission once (unless already denied); no second button
  // click is required. The user may decline and still submit the lead.
  useEffect(() => {
    let cancelled = false;
    const requestOnOpen = async () => {
      // React StrictMode replays mount effects in development. Yield once so
      // only the active mount requests permission, rather than prompting twice.
      await Promise.resolve();
      if (cancelled) return;

      if (navigator.permissions?.query) {
        try {
          const permission = await navigator.permissions.query({ name: "geolocation" });
          if (cancelled) return;
          if (permission.state === "denied") {
            setPermissionState("denied");
            setError("Location permission was denied. You can still submit your request.");
            return;
          }
        } catch {
          // Browsers without Permissions API may still support geolocation.
        }
      }

      if (!cancelled) requestLocation();
    };

    requestOnOpen();
    return () => { cancelled = true; };
    // Only request on the initial form opening, not on subsequent renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestLocation = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Your browser does not support location access.");
      setPermissionState("denied");
      return;
    }
    setLoading(true);
    setPermissionState("requesting");

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        onChange({
          latitude: coords.latitude,
          longitude: coords.longitude,
        });
        setLoading(false);
        setPermissionState("granted");
      },
      (err) => {
        setLoading(false);
        if (err.code === 1) {
          setPermissionState("denied");
          setError(
            "Location permission was denied. You can still submit your request."
          );
        } else {
          setPermissionState("idle");
          setError(
            "Unable to get your location. You can still submit your request."
          );
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  // Initialize and update map when location changes
  useEffect(() => {
    if (!location || !mapContainerRef.current) return;

    let L;
    let isCancelled = false;

    const initMap = async () => {
      setMapLoading(true);
      try {
        L = (await import("leaflet")).default;
        await import("leaflet/dist/leaflet.css");

        if (isCancelled) return;

        // Fix default marker icon paths
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
          iconUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
          shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
        });

        const { latitude, longitude } = location;

        if (!mapRef.current) {
          mapRef.current = L.map(mapContainerRef.current, {
            center: [latitude, longitude],
            zoom: 16,
            zoomControl: false,
            attributionControl: false,
            dragging: false,
            touchZoom: false,
            doubleClickZoom: false,
            scrollWheelZoom: false,
            boxZoom: false,
            keyboard: false,
            tap: false,
          });

          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
          }).addTo(mapRef.current);

          markerRef.current = L.marker([latitude, longitude]).addTo(
            mapRef.current
          );
        } else {
          mapRef.current.setView([latitude, longitude], 16);
          if (markerRef.current) {
            markerRef.current.setLatLng([latitude, longitude]);
          } else {
            markerRef.current = L.marker([latitude, longitude]).addTo(
              mapRef.current
            );
          }
        }

        setTimeout(() => {
          if (isCancelled) return;
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
          setMapLoading(false);
        }, 100);
      } catch (err) {
        if (!isCancelled) setMapLoading(false);
      }
    };

    initMap();

    return () => {
      isCancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
  }, [location]);

  const handleRemove = () => {
    onChange(null);
    setError("");
    setPermissionState("idle");
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
    }
  };

  const isGranted = permissionState === "granted" && location;

  return (
    <section
      className={`rounded-xl border p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex items-center justify-center rounded-full w-9 h-9 shrink-0 ${
            isGranted
              ? "bg-green-100 dark:bg-green-900/40"
              : "bg-blue-100 dark:bg-blue-900/40"
          }`}
        >
          {isGranted ? (
            <FiCheck className="text-green-600" size={18} />
          ) : (
            <FiMapPin className="text-blue-600" size={18} />
          )}
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-semibold">
            Allow location to reach out to you
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            It will help us identify your pickup details and make it easier for
            our team to reach you.
          </p>

          {/* ============ IDLE: Show CTA button ============ */}
          {permissionState === "idle" && !location && (
            <div className="mt-3">
              <button
                type="button"
                onClick={requestLocation}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800"
              >
                <FiNavigation size={14} />
                Allow Location
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Sharing is voluntary. You can continue without allowing
                location access.
              </p>
            </div>
          )}

          {/* ============ REQUESTING: Show loading ============ */}
          {permissionState === "requesting" && (
            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <span className="w-3.5 h-3.5 border-2 border-gray-400 rounded-full border-t-transparent animate-spin" />
              Requesting location permission...
            </div>
          )}

          {/* ============ GRANTED: Show map ============ */}
          {isGranted && (
            <div className="mt-3">
              <div className="relative">
                <div
                  ref={mapContainerRef}
                  className="w-full h-32 overflow-hidden border border-gray-200 rounded-lg dark:border-gray-700"
                  style={{ minHeight: "8rem" }}
                />
                {mapLoading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30">
                    <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  </div>
                )}
                {!mapLoading && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute px-2 py-1 text-xs text-gray-700 transition rounded shadow top-2 right-2 bg-white/90 dark:bg-gray-800/90 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700"
                  >
                    Remove
                  </button>
                )}
              </div>

              {!mapLoading && (
                <p
                  className="flex items-center gap-1 mt-2 text-xs text-green-600"
                  role="status"
                >
                  <FiCheck size={12} />
                  Location added successfully.
                </p>
              )}
            </div>
          )}

          {/* ============ ERROR / DENIED ============ */}
          {error && (
            <div className="mt-3">
              <p className="text-xs text-amber-600" role="alert">
                {error}
              </p>
              {permissionState === "denied" && !location && (
                <button
                  type="button"
                  onClick={requestLocation}
                  className="mt-2 text-xs font-medium text-blue-600 underline hover:text-blue-700"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {!isGranted && permissionState !== "idle" && !error && (
            <p className="mt-2 text-xs text-gray-500">
              Sharing is voluntary. You can continue without allowing location
              access.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default OptionalLocation;